import { defineStore } from 'pinia';
import type {
  CreateClientGroupMutation,
  CreateClientGroupMutationVariables,
  CreateClientGroupDto,
  DeleteClientGroupMutation,
  DeleteClientGroupMutationVariables,
  DeleteConfigMutation,
  DeleteConfigMutationVariables,
  DeleteStagingConfigMutation,
  DeleteStagingConfigMutationVariables,
  GetClientGroupsQuery,
  GetClientGroupsQueryVariables,
  GetClientGroupQuery,
  GetClientGroupQueryVariables,
  MoveClientGroupMutation,
  MoveClientGroupMutationVariables,
  SubClientGroupUpdatedSubscription,
  SubClientGroupUpdatedSubscriptionVariables,
  UpdateClientGroupMutation,
  UpdateClientGroupMutationVariables,
  UpdateClientGroupDto, Config,
} from '@/types/graphql';
import getClientGroups from '@/graphql/queries/getClientGroups.graphql';
import getClientGroup from '@/graphql/queries/getClientGroup.graphql';
import moveClientGroup from '@/graphql/mutations/moveClientGroup.graphql';
import updateClientGroup from '@/graphql/mutations/updateClientGroup.graphql';
import deleteClientGroup from '@/graphql/mutations/deleteClientGroup.graphql';
import deleteConfig from '@/graphql/mutations/deleteConfig.graphql';
import deleteStagingConfig from '@/graphql/mutations/deleteStagingConfig.graphql';
import createClientGroup from '@/graphql/mutations/createClientGroup.graphql';
import subClientGroupUpdated from '@/graphql/subscriptions/subClientGroupUpdated.graphql';
import subConfigUpdated from '@/graphql/subscriptions/subConfigUpdated.graphql';
import storeGraphqlQuery from '~/composables/storeGraphqlQuery';
import storeGraphqlMutation from '~/composables/storeGraphqlMutation';
import storeGraphqlSubscription from '~/composables/storeGraphqlSubscription';
import promoteStagingConfig from '@/graphql/mutations/promoteStagingConfig.graphql';
import type { PromoteStagingConfigMutation, PromoteStagingConfigMutationVariables } from '~/types/graphql';

type ClientGroupListItem = GetClientGroupsQuery['getClientGroups'][number];

/**
 * A Pinia store for managing client groups.
 */
export const useClientGroupsStore = defineStore("clientGroups", {
  state: () => {
    return {
      groups: [] as ClientGroupListItem[],
      loading: false,
      listLoading: false,
      detailLoading: false,
      configSubscription: null as any,
      groupsSubscription: null as any,
      getGroupsInFlight: null as Promise<any> | null,
    };
  },
  actions: {
    /**
     * Sets the groups in the store.
     *
     * @param {ClientGroupListItem[]} groups - The groups to set.
     */
    setGroups(groups: ClientGroupListItem[]): void {
      this.groups = groups;
    },
    /**
     * Fetches the groups from the server and subscribes to group updates.
     *
     * @returns A promise that resolves when the groups are fetched and the subscription is set up.
     */
    async getGroups() {
      if (this.getGroupsInFlight) {
        return this.getGroupsInFlight;
      }

      this.getGroupsInFlight = (async () => {
        this.loading = true;
        this.listLoading = true;

        const res = await storeGraphqlQuery<
          GetClientGroupsQuery,
          GetClientGroupsQueryVariables
        >(
          {
            query: getClientGroups,
            variables: {},
          },
          "Fehler beim Laden der Gruppen"
        );

        if (!this.groupsSubscription) {
          const observable = await storeGraphqlSubscription<
            SubClientGroupUpdatedSubscription,
            SubClientGroupUpdatedSubscriptionVariables
          >({
            query: subClientGroupUpdated,
          });

          if (observable) {
            this.groupsSubscription = observable.subscribe({
              complete: () => {
                console.log("complete");
              },
              error: (error) => {
                console.log(error);
              },
              start: () => {
                console.log("start");
              },
              next: (data) => {
                console.log(data);
                const subData = data?.data?.subClientGroupUpdated;
                if (!subData) return;

                if (subData.updated?.length) {
                  subData.updated.forEach((updatedGroup) => {
                    const index = this.groups.findIndex(
                      (group) => group.id === updatedGroup.id
                    );
                    if (index > -1) {
                      this.groups[index] = updatedGroup;
                    }
                  });
                  // Force reassignment for reactivity
                  this.groups = [...this.groups];
                } else if (subData.created?.length) {
                  subData.created.forEach((createdGroup) => {
                    this.groups.push(createdGroup);
                  });
                  this.groups = [...this.groups];
                } else if (subData.deleted) {
                  subData.deleted.forEach((deletedGroup) => {
                    const index = this.groups.findIndex(
                      (group) => group.id === deletedGroup
                    );
                    if (index > -1) {
                      this.groups.splice(index, 1);
                    }
                  });
                  this.groups = [...this.groups];
                }
              },
            });
          }
        }

        // Subscribe to config updates to refresh groups with changed configs
        if (!this.configSubscription) {
          const configObservable = await storeGraphqlSubscription({
            query: subConfigUpdated,
          });

          if (configObservable) {
            this.configSubscription = configObservable.subscribe({
              next: async (data) => {
                const subData = data?.data?.subConfigUpdated;
                if (!subData?.updated?.length) return;

                // Collect IDs of groups whose config changed so we can
                // invalidate package comparisons for their clients.
                const affectedGroupIds: number[] = [];

                subData.updated.forEach((updatedConfig: Config) => {
                  this.groups.forEach((group) => {
                    if (group.config?.id === updatedConfig.id) {
                      // Merge updated config fields into the group
                      group.config = { ...group.config, ...updatedConfig };
                      if (group.id != null) affectedGroupIds.push(group.id);
                    }
                  });
                });

                // Force array reassignment so Vue's reactivity picks up
                // the nested config hash changes immediately.
                this.groups = [...this.groups];

                // Invalidate and re-fetch package comparisons for every
                // client that belongs to one of the updated groups.
                if (affectedGroupIds.length) {
                  // Lazy import to avoid circular dependency
                  const { useClientsStore } = await import('~/stores/clients');
                  const clientsStore = useClientsStore();
                  clientsStore.clients.forEach((client) => {
                    if (client.id != null && client.clientGroupId != null && affectedGroupIds.includes(client.clientGroupId)) {
                      const group = this.groups.find((g) => g.id === client.clientGroupId);
                      const configId = group?.config?.id;
                      if (configId) {
                        clientsStore.invalidatePackageComparison(client.id);
                        clientsStore.fetchPackageComparison(client.id, configId);
                      }
                    }
                  });
                }
              },
              error: (error) => {
                console.error("Config subscription error:", error);
              },
            });
          }
        }

        if (res?.data) {
          this.setGroups(res.data.getClientGroups);
        }
        this.loading = false;
        this.listLoading = false;
        return res;
      })();

      try {
        return await this.getGroupsInFlight;
      } finally {
        this.getGroupsInFlight = null;
      }
    },
    /**
     * Fetches a single group by ID.
     *
     * @param groupId The ID of the group to fetch.
     * @returns A promise that resolves with the group data.
     */
    async getGroup(groupId: number) {
      this.detailLoading = true;

      try {
        const res = await storeGraphqlQuery<
          GetClientGroupQuery,
          GetClientGroupQueryVariables
        >(
          {
            query: getClientGroup,
            variables: {
              id: groupId,
            },
          },
          "Error while loading the group"
        );
        this.detailLoading = false;
        return res;
      } catch (error) {
        this.detailLoading = false;
        throw error;
      }
    },
    async moveGroup(groupId: number, parentId: number | null) {
      return await storeGraphqlMutation<
        MoveClientGroupMutation,
        MoveClientGroupMutationVariables
      >({
        mutation: moveClientGroup,
        variables: {
          groupId,
          parentId,
        },
      });
    },
    /**
     * Updates a group with the given data.
     *
     * @param updateClientGroupDto The data to update the group with.
     * @returns The result of the update operation.
     */
    async updateGroup(updateClientGroupDto: UpdateClientGroupDto) {
      this.loading = true;
      this.listLoading = true;

      try {
        const res = await storeGraphqlMutation<
          UpdateClientGroupMutation,
          UpdateClientGroupMutationVariables
        >({
          mutation: updateClientGroup,
          variables: {
            ...updateClientGroupDto,
          },
        });

        const updatedGroup = res?.data?.updateClientGroup;
        if (updatedGroup) {
          const index = this.groups.findIndex((group) => group.id === updatedGroup.id);
          if (index > -1) {
            this.groups[index] = {
              ...this.groups[index],
              ...updatedGroup,
            };
            this.groups = [...this.groups];
          }
        }

        return res;
      } finally {
        this.loading = false;
        this.listLoading = false;
      }
    },
    /**
     * Deletes a group with the given ID.
     *
     * @param groupId The ID of the group to delete.
     * @returns The result of the delete operation.
     */
    async deleteGroup(groupId: number) {
      this.loading = true;
      this.listLoading = true;

      try {
        const res = await storeGraphqlMutation<
          DeleteClientGroupMutation,
          DeleteClientGroupMutationVariables
        >(
          {
            mutation: deleteClientGroup,
            variables: {
              groupId: groupId,
            },
          },
        );
        await this.getGroups();

        this.loading = false;
        this.listLoading = false;

        return res;
      } catch (error) {
        this.loading = false;
        this.listLoading = false;
        throw error;
      }
    },
    /**
     * Re-fetches a single group by ID and merges it into the store.
     * Use this to pick up changes (e.g. a newly pulled staging config)
     * without a full getGroups() reload.
     *
     * @param groupId The ID of the group to refresh.
     */
    async refreshGroupInStore(groupId: number) {
      try {
        const res = await storeGraphqlQuery<
          GetClientGroupQuery,
          GetClientGroupQueryVariables
        >(
          { query: getClientGroup, variables: { id: groupId } },
          'Error while refreshing group',
        );
        const fresh = res?.data?.getClientGroup;
        if (!fresh) return;

        const idx = this.groups.findIndex((g) => g.id === groupId);
        if (idx > -1) {
          this.groups[idx] = { ...this.groups[idx], ...fresh };
        } else {
          this.groups.push(fresh);
        }
        this.groups = [...this.groups];
      } catch (error) {
        console.error('Error refreshing group in store:', error);
      }
    },

    /**
     * Deletes the configuration from a group with the given ID.
     *
     * @param groupId The ID of the group to delete the configuration from.
     * @return The result of the delete operation.
     */
    async deleteConfigFromGroup(groupId: number) {
      return await storeGraphqlMutation<
        DeleteConfigMutation,
        DeleteConfigMutationVariables
      >({
        mutation: deleteConfig,
        variables: { groupId },
      });
    },

    /**
     * Deletes the staging configuration from a group with the given ID.
     * This nullifies the stagingConfigId FK and prunes the orphaned Config record.
     *
     * @param groupId The ID of the group to delete the staging configuration from.
     * @return The result of the delete operation.
     */
    async deleteStagingConfigFromGroup(groupId: number) {
      return await storeGraphqlMutation<
        DeleteStagingConfigMutation,
        DeleteStagingConfigMutationVariables
      >({
        mutation: deleteStagingConfig,
        variables: { groupId },
      });
    },

    async promoteStagingConfigToProduction(groupId: number) {
      return await storeGraphqlMutation<
        PromoteStagingConfigMutation,
        PromoteStagingConfigMutationVariables
      >({
        mutation: promoteStagingConfig,
        variables: {
          clientGroupId: groupId
        },
      });
    },

    /**
     * Creates a new group with the given data.
     *
     * @param createClientGroupDto The data to create the new group with.
     * @return The result of the create operation.
     */
    async createGroup(createClientGroupDto: CreateClientGroupDto) {
      this.loading = true;
      this.listLoading = true;

      const res = await storeGraphqlMutation<
        CreateClientGroupMutation,
        CreateClientGroupMutationVariables
      >({
        mutation: createClientGroup,
        variables: {
          ...createClientGroupDto,
        },
      });
      await this.getGroups();

      this.loading = false;
      this.listLoading = false;

      return res;
    },
  },
});

